import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { motionValue } from 'framer-motion'
import LyricStageIsland from '../src/island/LyricStageIsland'
import { createEmptyAudioBands } from '../vendor/folia-src/host/createAudioBands'
import { lyricPayloadToLyricData } from '../src/lyrics/lyricPayloadAdapter'
import { JizuraProvider } from '../src/jizura/JizuraSettings'
import {
  isStageMessage,
  makeEnvelope,
  M2S,
  S2M,
  type AudioBandsPayload,
  type ControlPayload,
  type PvStatusPayload,
  type QueueItem,
  type LyricPayload,
  type SessionPayload,
  type TimeTickPayload,
} from './protocol'
import { nextPausedState, shouldAnnounceReadyAfterSessionInit } from './bridgeReducers'
import type { LyricData } from '../vendor/folia-src/types'
import type { LyricStageRecordStatus } from '../vendor/folia-src/host/chrome/LyricStagePlayerChrome'
import '../src/lyric-stage.css'

type BridgeState = {
  active: boolean
  lyrics: LyricData
  lyricPayload: LyricPayload
  coverUrl: string | null
  songTitle: string
  songArtist: string
  duration: number
  paused: boolean
  tracks: QueueItem[]
  currentTrackId: string | null
  recordSupported: boolean
  recordStatus: LyricStageRecordStatus | null
}

const emptyLyrics = (): LyricData => ({ lines: [] })

function parentOriginFromQuery(): string {
  try {
    const q = new URLSearchParams(window.location.search)
    const raw = q.get('parentOrigin') || ''
    if (raw) return new URL(raw).origin
  } catch {
    /* ignore */
  }
  return ''
}

function applySession(prev: BridgeState, session: SessionPayload): BridgeState {
  return {
    ...prev,
    active: true,
    lyrics: lyricPayloadToLyricData(session.lyricPayload),
    lyricPayload: session.lyricPayload || { kind: 'empty' },
    coverUrl: session.coverUrl || null,
    songTitle: session.title || '',
    songArtist: session.artist || '',
    duration: Math.max(0, Number(session.durationMs) || 0) / 1000,
    paused: Boolean(session.paused),
    tracks: Array.isArray(session.queue) ? session.queue : [],
    currentTrackId: session.currentTrackId ?? null,
    recordSupported: Boolean(session.recordSupported),
  }
}

const SOURCE_URL = (
  String(import.meta.env.VITE_STAGE_SOURCE_URL || '').trim()
  || 'https://github.com/wangxuetong123456/musey-lyric-stage'
)

function AgplSourceLink() {
  return (
    <a
      className="lyric-stage-source-link"
      href={SOURCE_URL}
      target="_blank"
      rel="noopener noreferrer"
      title="AGPL-3.0 Corresponding Source"
    >
      源码 · AGPL-3.0
    </a>
  )
}

const BridgeApp: React.FC = () => {
  const parentOriginRef = useRef(parentOriginFromQuery())
  const currentTime = useMemo(() => motionValue(0), [])
  const bandsBundle = useMemo(() => createEmptyAudioBands(), [])
  const { audioPower, audioBands } = bandsBundle

  const lastTickRef = useRef<TimeTickPayload | null>(null)
  const rafRef = useRef(0)

  const [state, setState] = useState<BridgeState>({
    active: false,
    lyrics: emptyLyrics(),
    lyricPayload: { kind: 'empty' },
    coverUrl: null,
    songTitle: '',
    songArtist: '',
    duration: 0,
    paused: true,
    tracks: [],
    currentTrackId: null,
    recordSupported: false,
    recordStatus: null,
  })

  const postToParent = useCallback((type: string, payload: unknown = {}) => {
    const origin = parentOriginRef.current
    if (!origin || !window.parent || window.parent === window) return
    window.parent.postMessage(makeEnvelope(type, payload), origin)
  }, [])

  const extrapolateClock = useCallback(() => {
    const tick = lastTickRef.current
    if (!tick) {
      rafRef.current = requestAnimationFrame(extrapolateClock)
      return
    }
    let seconds = tick.positionMs / 1000
    if (!tick.paused) {
      const drift = (performance.now() - tick.sampledAtMs) / 1000
      seconds += Math.max(0, drift)
    }
    currentTime.set(seconds)
    rafRef.current = requestAnimationFrame(extrapolateClock)
  }, [currentTime])

  useEffect(() => {
    rafRef.current = requestAnimationFrame(extrapolateClock)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [extrapolateClock])

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!isStageMessage(event.data)) return

      // First trusted parent message locks the origin if not provided via query.
      if (!parentOriginRef.current) {
        parentOriginRef.current = event.origin
      } else if (event.origin !== parentOriginRef.current) {
        return
      }

      const { type, payload } = event.data

      if (type === M2S.SESSION_INIT || type === M2S.TRACK_CHANGED) {
        const session = payload as SessionPayload
        lastTickRef.current = {
          positionMs: Number(session.positionMs) || 0,
          sampledAtMs: performance.now(),
          paused: Boolean(session.paused),
        }
        currentTime.set((Number(session.positionMs) || 0) / 1000)
        setState((prev) => applySession(prev, session))
        if (type === M2S.SESSION_INIT && shouldAnnounceReadyAfterSessionInit()) {
          postToParent(S2M.READY, {})
        }
        return
      }

      if (type === M2S.SESSION_END) {
        lastTickRef.current = null
        setState((prev) => ({
          ...prev,
          active: false,
          recordStatus: null,
        }))
        return
      }

      if (type === M2S.QUEUE_UPDATED) {
        const q = payload as { queue?: QueueItem[]; currentTrackId?: string | null }
        setState((prev) => ({
          ...prev,
          tracks: Array.isArray(q.queue) ? q.queue : prev.tracks,
          currentTrackId: q.currentTrackId ?? prev.currentTrackId,
        }))
        return
      }

      if (type === M2S.TIME_TICK) {
        const tick = payload as TimeTickPayload
        const paused = Boolean(tick.paused)
        lastTickRef.current = {
          positionMs: Number(tick.positionMs) || 0,
          sampledAtMs: Number(tick.sampledAtMs) || performance.now(),
          paused,
        }
        setState((prev) => (
          nextPausedState(prev.paused, paused) === null ? prev : { ...prev, paused }
        ))
        return
      }

      if (type === M2S.AUDIO_BANDS) {
        const b = payload as AudioBandsPayload
        audioPower.set(Number(b.power) || 0)
        audioBands.bass.set(Number(b.bass) || 0)
        audioBands.lowMid.set(Number(b.lowMid) || 0)
        audioBands.mid.set(Number(b.mid) || 0)
        audioBands.vocal.set(Number(b.vocal) || 0)
        audioBands.treble.set(Number(b.treble) || 0)
        return
      }

      if (type === M2S.PV_STATUS) {
        const status = payload as PvStatusPayload
        setState((prev) => {
          const recordStatus = {
            phase: status.phase as LyricStageRecordStatus['phase'],
            countdown: status.countdown,
            elapsed: status.elapsed,
            duration: status.duration,
            error: status.error,
          }
          const prevStatus = prev.recordStatus
          if (
            prevStatus
            && prevStatus.phase === recordStatus.phase
            && prevStatus.countdown === recordStatus.countdown
            && prevStatus.elapsed === recordStatus.elapsed
            && prevStatus.duration === recordStatus.duration
            && prevStatus.error === recordStatus.error
          ) {
            return prev
          }
          return { ...prev, recordStatus }
        })
      }
    }

    window.addEventListener('message', onMessage)
    // Announce readiness for preloaded iframes (parent may send SESSION_INIT later).
    if (parentOriginRef.current) {
      postToParent(S2M.READY, { boot: true })
    }
    return () => window.removeEventListener('message', onMessage)
  }, [audioBands, audioPower, currentTime, postToParent])

  const sendControl = useCallback((control: ControlPayload) => {
    postToParent(S2M.CONTROL, control)
  }, [postToParent])

  const recordingBusy = Boolean(
    state.recordStatus
    && (state.recordStatus.phase === 'preparing'
      || state.recordStatus.phase === 'countdown'
      || state.recordStatus.phase === 'recording'
      || state.recordStatus.phase === 'stopping'),
  )

  if (!state.active) {
    return (
      <div className="lyric-stage-boot">
        <p>Musey Lyric Stage</p>
        <p className="lyric-stage-boot-sub">Waiting for host session…</p>
        <AgplSourceLink />
      </div>
    )
  }

  return (
    <div className="lyric-stage-shell">
      <JizuraProvider lyricPayload={state.lyricPayload} duration={state.duration} recordingBusy={recordingBusy}>
        <LyricStageIsland
          lyrics={state.lyrics}
          coverUrl={state.coverUrl}
          songTitle={state.songTitle}
          songArtist={state.songArtist}
          duration={state.duration}
          paused={state.paused}
          currentTime={currentTime}
          audioPower={audioPower}
          audioBands={audioBands}
          tracks={state.tracks}
          currentTrackId={state.currentTrackId}
          chromeHidden={recordingBusy}
          recordSupported={state.recordSupported}
          recordStatus={state.recordStatus}
          showRecordHud={false}
          panelFooter={<AgplSourceLink />}
          onTogglePlay={() => {
            if (recordingBusy) return
            sendControl({ action: 'toggle' })
          }}
          onSeek={(time) => {
            if (recordingBusy) return
            sendControl({ action: 'seek', positionMs: Math.max(0, time) * 1000 })
          }}
          onSelectTrack={(id) => {
            if (recordingBusy) return
            sendControl({ action: 'selectTrack', trackId: id })
          }}
          onPrevTrack={() => {
            if (recordingBusy) return
            sendControl({ action: 'prev' })
          }}
          onNextTrack={() => {
            if (recordingBusy) return
            sendControl({ action: 'next' })
          }}
          onExitLyricStage={() => postToParent(S2M.EXIT, {})}
          onStartPvRecord={() => postToParent(S2M.START_PV, {})}
          onCancelPvRecord={() => postToParent(S2M.CANCEL_PV, {})}
        />
      </JizuraProvider>
    </div>
  )
}

export function mountBridgeApp(el: HTMLElement): Root {
  const root = createRoot(el)
  root.render(
    <React.StrictMode>
      <BridgeApp />
    </React.StrictMode>,
  )
  return root
}
